import React from 'react';
import { AlertCircle, Loader as LoaderIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import ChannelCard from './ChannelCard';
import ChannelCardSkeleton from './ChannelCardSkeleton';
import EmptyState from './EmptyState';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';

/**
 * NicheFinderGrid — grid responsivo com infinite scroll (Sprint 3 v3).
 *
 * Estados:
 *   - Primeiro fetch (loading + lista vazia): 6 skeletons.
 *   - Erro inicial (sem dados): banner + botao retry.
 *   - Vazio (filtros zeram a lista): EmptyState.
 *   - Com dados: grid + sentinela no fim que dispara loadMore via
 *     IntersectionObserver.
 *
 * Container queries (.cq-grid-3) entregam 3 colunas em desktop -> 2/1.
 */
const NicheFinderGrid = ({
  channels,
  loading,
  error,
  onRetry,
  hasActiveFilters,
  onClearFilters,
  hasMore,
  loadMore,
  visitedList,
  isSuperAdmin,
  isPrivacyMode,
  onVisit,
  onDelete,
  onReenrich,
  isFavorited,
  onToggleFavorite,
}) => {
  const { t } = useTranslation();
  const sentinelRef = useInfiniteScroll(loadMore, hasMore, loading);

  // ===== Primeiro fetch =====
  if (loading && (!channels || channels.length === 0)) {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 12,
          alignItems: 'stretch',
        }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <ChannelCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  // ===== Erro inicial =====
  if (error && (!channels || channels.length === 0)) {
    // 429 = limite de requisições por minuto (uso muito rápido) — mensagem
    // própria: não é falha do sistema, basta aguardar e tentar de novo.
    const isRateLimited = error?.response?.status === 429;
    return (
      <div
        className="dp-card"
        style={{
          padding: 24,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <AlertCircle size={32} color={isRateLimited ? 'var(--dp-yellow-500, #F59E0B)' : 'var(--dp-red-500)'} />
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--dp-text-primary)',
            fontFamily: 'var(--dp-font)',
          }}
        >
          {isRateLimited ? t('viralNiches.rate_limited_title') : t('viralNiches.load_error_title')}
        </div>
        {isRateLimited && (
          <div
            style={{
              fontSize: 12.5,
              color: 'var(--dp-text-secondary)',
              fontFamily: 'var(--dp-font)',
              maxWidth: 360,
              lineHeight: 1.5,
            }}
          >
            {t('viralNiches.rate_limited_message')}
          </div>
        )}
        <button type="button" className="dp-btn-secondary" onClick={onRetry}>
          {t('viralNiches.load_error_retry')}
        </button>
      </div>
    );
  }

  // ===== Vazio =====
  if (!channels || channels.length === 0) {
    return (
      <EmptyState
        hasActiveFilters={hasActiveFilters}
        onClearFilters={onClearFilters}
      />
    );
  }

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 12,
          alignItems: 'stretch',
        }}
      >
        {channels.map((ch, idx) => (
          <div
            key={ch.channel_id}
            className="dp-card-stagger-in"
            style={{ animationDelay: `${(idx % 3) * 60}ms`, height: '100%' }}
          >
            <ChannelCard
              channel={ch}
              isVisited={Array.isArray(visitedList) && visitedList.includes(ch.channel_id)}
              isPrivacyMode={isPrivacyMode}
              isSuperAdmin={isSuperAdmin}
              isFavorited={typeof isFavorited === 'function' ? isFavorited(ch.channel_id) : false}
              onVisit={onVisit}
              onDelete={onDelete}
              onReenrich={onReenrich}
              onToggleFavorite={onToggleFavorite}
            />
          </div>
        ))}
      </div>

      {/* Sentinela do infinite scroll — IntersectionObserver dispara
          loadMore quando este div entra no viewport (com 300px de antecipacao). */}
      {hasMore && (
        <div
          ref={sentinelRef}
          style={{
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            color: 'var(--dp-text-tertiary)',
            fontSize: 12,
            fontFamily: 'var(--dp-font)',
            marginTop: 16,
          }}
        >
          <LoaderIcon
            size={14}
            className="dp-spin"
            style={{ color: 'var(--dp-blue-500)' }}
          />
          <span>{t('viralNiches.loading_more')}</span>
        </div>
      )}
    </>
  );
};

export default NicheFinderGrid;
